import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, resolveMediaUrl, salonVideoApiPath } from '../api/client';
import { readFranchiseFlowAuth } from '../../shared/franchiseFlowAuth';
import { reelsPath } from '../paths';
import { persistReelCheckinId } from '../utils/reelCheckin';
import {
  fetchReelEligibleGuests,
  guestHasName,
  hasStartedService,
  reelGuestSubtitle,
} from '../utils/reelEligibleGuests';

const SERVICES = [
  { id: 'haircut', title: 'Haircut', blurb: 'Cut, style, reveal' },
  { id: 'hair_coloring', title: 'Hair Coloring', blurb: 'Color journey, soft reveal' },
  { id: 'facial', title: 'Facial', blurb: 'Clean glow, elegant finish' },
  { id: 'nails', title: 'Nail Service', blurb: 'Macro detail, perfect tips' },
];

/**
 * New capture: pick guest (service started on desk) → service type → style → record.
 */
export default function ServiceSelect() {
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('guest'); // guest | service | options
  const [serviceId, setServiceId] = useState('');
  const [musicTracks, setMusicTracks] = useState([]);
  const [musicTrack, setMusicTrack] = useState('pleasant_01');
  const [textColors, setTextColors] = useState([]);
  const [reelTextColor, setReelTextColor] = useState('white');
  const [guests, setGuests] = useState([]);
  const [guestsLoading, setGuestsLoading] = useState(true);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [previewTrackId, setPreviewTrackId] = useState(null);
  const previewAudioRef = useRef(null);

  const checkinId = selectedGuest?.id ? String(selectedGuest.id) : '';

  useEffect(() => {
    const audio = previewAudioRef.current;
    return () => {
      if (audio) {
        audio.pause();
        audio.src = '';
      }
    };
  }, []);

  useEffect(() => {
    if (step !== 'options' && previewAudioRef.current) {
      previewAudioRef.current.pause();
      setPreviewTrackId(null);
    }
  }, [step]);

  function trackPreviewUrl(track) {
    const raw =
      track?.preview_url || `${salonVideoApiPath()}/music-preview/${track?.id || ''}`;
    return resolveMediaUrl(raw);
  }

  function toggleTrackPreview(track, event) {
    event.stopPropagation();
    event.preventDefault();
    if (!track?.id) return;
    const url = trackPreviewUrl(track);
    if (!url) return;
    let audio = previewAudioRef.current;
    if (!audio) {
      audio = new Audio();
      audio.onended = () => setPreviewTrackId(null);
      previewAudioRef.current = audio;
    }
    if (previewTrackId === track.id && !audio.paused) {
      audio.pause();
      setPreviewTrackId(null);
      return;
    }
    audio.pause();
    audio.src = url;
    audio.currentTime = 0;
    audio
      .play()
      .then(() => setPreviewTrackId(track.id))
      .catch(() => setPreviewTrackId(null));
  }

  useEffect(() => {
    const fromState = location.state?.checkinId || location.state?.checkin?.id;
    const preloaded = location.state?.checkin;
    const presentationReels = Boolean(location.state?.presentationReels);
    if (fromState && preloaded && String(preloaded.id) === String(fromState)) {
      if (presentationReels && (!guestHasName(preloaded) || !hasStartedService(preloaded))) {
        setError('This guest does not have a started service yet. Start service on the desk first.');
        setStep('guest');
        return;
      }
      setSelectedGuest(preloaded);
      setStep('service');
    } else if (fromState) {
      const stub = { id: fromState, customer_name: location.state?.guestName || '' };
      if (presentationReels) {
        setError('Could not verify started service for this guest.');
        setStep('guest');
        return;
      }
      setSelectedGuest(stub);
      setStep('service');
    }
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;
    api
      .musicOptions()
      .then((data) => {
        if (cancelled) return;
        const tracks = Array.isArray(data?.tracks) ? data.tracks : [];
        const colors = Array.isArray(data?.text_colors) ? data.text_colors : [];
        setMusicTracks(tracks);
        setTextColors(colors);
        if (tracks.length) {
          setMusicTrack((prev) => (tracks.some((t) => t.id === prev) ? prev : tracks[0].id));
        }
        if (colors.length) {
          setReelTextColor((prev) => (colors.some((c) => c.id === prev) ? prev : colors[0].id));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMusicTracks([
            { id: 'pleasant_01', label: 'Sunlit salon' },
            { id: 'sh_02', label: 'Soft horizon' },
            { id: 'pleasant_02', label: 'Gentle glow' },
          ]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadGuests = () => {
    setGuestsLoading(true);
    setError('');
    fetchReelEligibleGuests()
      .then((rows) => setGuests(rows))
      .catch((e) => setError(e?.message || 'Could not load guests'))
      .finally(() => setGuestsLoading(false));
  };

  useEffect(() => {
    if (step === 'guest') loadGuests();
  }, [step]);

  function pickGuest(checkin) {
    setSelectedGuest(checkin);
    persistReelCheckinId(checkin.id);
    setStep('service');
    setError('');
  }

  function pickService(id) {
    setServiceId(id);
    setStep('options');
    setError('');
  }

  async function start() {
    if (!checkinId) {
      setError('Select a guest with a started service.');
      return;
    }
    if (!guestHasName(selectedGuest)) {
      setError('Guest name is required on the salon desk before recording a reel.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const auth = readFranchiseFlowAuth() || {};
      const session = await api.createSession(serviceId, 'premium', {
        franchise_name: auth.franchise_name || '',
        org_logo_url: auth.org_logo || '',
        music_track: musicTrack,
        reel_text_color: reelTextColor,
        checkin_id: checkinId,
      });
      try {
        sessionStorage.setItem('naturals_video_last_session', session.id);
      } catch {
        /* ignore */
      }
      navigate(reelsPath('capture', session.id), { state: { service_type: serviceId } });
    } catch (e) {
      setError(e.message || 'Could not start session');
    } finally {
      setBusy(false);
    }
  }

  const guestLabel = selectedGuest
    ? [selectedGuest.customer_name, selectedGuest.customer_code].filter(Boolean).join(' · ')
    : '';

  if (step === 'guest') {
    return (
      <main className="service-select-page">
        <p className="eyebrow">Naturals Salon</p>
        <h1 className="brand">Choose guest</h1>
        <p className="lede">
          Only guests with a <strong>name</strong> and <strong>started service</strong> on the salon desk
          appear here. Reels are saved under the guest name for easy download.
        </p>
        {error ? <div className="error-banner">{error}</div> : null}
        {guestsLoading ? <p className="lede">Loading guests…</p> : null}
        {!guestsLoading && !guests.length ? (
          <p className="lede">
            No eligible guests. Add the guest name and start a service on Salon hub, then refresh.
          </p>
        ) : null}
        <div className="reel-guest-list" role="list">
          {guests.map((g) => (
            <button
              key={g.id}
              type="button"
              role="listitem"
              className="reel-guest-row"
              onClick={() => pickGuest(g)}
            >
              <span className="reel-guest-row__main">
                <strong>{g.customer_name || 'Guest'}</strong>
                {g.customer_code ? <span className="reel-guest-row__code">{g.customer_code}</span> : null}
              </span>
              <span className="reel-guest-row__sub">{reelGuestSubtitle(g) || 'Service started'}</span>
              {g.mobile ? <span className="reel-guest-row__mobile">{g.mobile}</span> : null}
            </button>
          ))}
        </div>
        <button className="service-select-link" type="button" onClick={loadGuests} disabled={guestsLoading}>
          Refresh guest list
        </button>
        <button className="service-select-link" type="button" onClick={() => navigate(reelsPath())}>
          ← Back to reels home
        </button>
      </main>
    );
  }

  if (step === 'options') {
    return (
      <main className="service-select-page service-select-page--share">
        <p className="eyebrow">Reel setup</p>
        <h1 className="brand">Style & record</h1>
        <div className="panel share-panel">
          <p className="service-select-section-label">Guest</p>
          <div className="reel-guest-banner">
            <strong>{guestLabel || 'Guest'}</strong>
            {selectedGuest?.mobile ? <span>Mobile: {selectedGuest.mobile}</span> : null}
          </div>
          <p className="service-select-section-label">Reel music</p>
          <div className="music-mood-grid music-mood-grid--tracks" role="list">
            {musicTracks.map((track) => {
              const isPlaying = previewTrackId === track.id;
              return (
                <div key={track.id} className="music-track-row" role="listitem">
                  <button
                    type="button"
                    className={`music-mood-btn${musicTrack === track.id ? ' on' : ''}`}
                    onClick={() => setMusicTrack(track.id)}
                  >
                    {track.label}
                    <span>Background track</span>
                  </button>
                  <button
                    type="button"
                    className={`music-track-preview-btn${isPlaying ? ' is-playing' : ''}`}
                    aria-label={isPlaying ? `Pause preview of ${track.label}` : `Preview ${track.label}`}
                    title={isPlaying ? 'Pause preview' : 'Preview track'}
                    onClick={(e) => toggleTrackPreview(track, e)}
                  >
                    {isPlaying ? '⏸' : '▶'}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="service-select-section-label">On-video text color</p>
          <div className="reel-text-color-grid" role="list">
            {(textColors.length
              ? textColors
              : [{ id: 'white', label: 'Classic white', hex: '#FFFFFF' }]
            ).map((color) => (
              <button
                key={color.id}
                type="button"
                role="listitem"
                className={`reel-text-color-btn${reelTextColor === color.id ? ' on' : ''}`}
                onClick={() => setReelTextColor(color.id)}
                aria-label={color.label}
              >
                <span className="reel-text-color-swatch" style={{ background: color.hex }} aria-hidden="true" />
                <span className="reel-text-color-label">{color.label}</span>
              </button>
            ))}
          </div>
        </div>
        {error ? <div className="error-banner">{error}</div> : null}
        <div className="service-select-cta-stack">
          <button className="btn btn-primary service-select-cta" disabled={busy} type="button" onClick={start}>
            {busy ? 'Starting…' : 'Start recording'}
          </button>
          <button className="service-select-link" disabled={busy} type="button" onClick={() => setStep('service')}>
            ← Change service
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="service-select-page">
      <p className="eyebrow">Naturals Salon</p>
      <h1 className="brand">Choose service</h1>
      {guestLabel ? <p className="service-select-guest-chip">Guest: {guestLabel}</p> : null}
      <p className="service-select-section-label">Transformation type</p>
      <div className="service-grid" role="list">
        {SERVICES.map((service) => (
          <button
            key={service.id}
            className="service-btn service-btn--cta"
            disabled={busy}
            type="button"
            onClick={() => pickService(service.id)}
          >
            <span className="service-btn-copy">
              <h3>{service.title}</h3>
              <p>{service.blurb}</p>
            </span>
            <span className="service-btn-go" aria-hidden="true">Next</span>
          </button>
        ))}
      </div>
      <button className="service-select-link" type="button" onClick={() => setStep('guest')}>
        ← Change guest
      </button>
    </main>
  );
}
